import cn from 'classnames';
import { FC, ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useBlocker } from 'react-router';

import type {
  ConsultationStatus,
  Document,
  DocumentSectionUpdateRequest,
} from '@/shared/api/transcriptor';
import { Badge, BadgeTone, Button, Textarea } from '@/shared/ui';

import { ScoreBadge, transcriptorMessages } from '@/entities/transcriptor';

import {
  formatTimecode,
  getSectionCitationStarts,
  getSectionText,
  showTranscriptQualityFirst,
} from '../lib';

import styles from './DocumentEditor.module.scss';
import { DocumentHeader, DocumentRow } from './DocumentRow';
import { InlineConfirm } from './InlineConfirm';

type Props = {
  document: Document;
  canEdit: boolean;
  requirement?: 'always' | 'on_demand';
  // Further tools of the header, shown while the text is not edited.
  actions?: ReactNode;
  className?: string;
  onSave: (sections: DocumentSectionUpdateRequest[]) => Promise<void>;
  onDirtyChange: (dirty: boolean) => void;
  onRegenerate?: () => Promise<void>;
  // The status the encounter's header right above already shows.
  encounterStatus?: ConsultationStatus;
};

export const DocumentEditor: FC<Props> = ({
  document,
  canEdit,
  requirement,
  actions,
  className,
  onSave,
  onDirtyChange,
  onRegenerate,
  encounterStatus,
}) => {
  const { formatMessage } = useIntl();
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateFailed, setRegenerateFailed] = useState(false);
  const sections = showTranscriptQualityFirst(
    document.type,
    document.sections ?? [],
    (section) => section.code,
  );
  const title = document.name || document.tab_title || document.code || '';
  const overallScore = document.overall_score;
  const isGenerating =
    document.status === 'pending' || document.status === 'generating';
  const getStatusBadge = (): { tone: BadgeTone; label: string } | null => {
    if (isGenerating) {
      return {
        tone: 'violet',
        label: formatMessage(transcriptorMessages.statusProcessing),
      };
    }
    if (document.status === 'failed') {
      return {
        tone: 'danger',
        label: formatMessage(transcriptorMessages.statusFailed),
      };
    }
    if (document.status === 'skipped') {
      return {
        tone: 'neutral',
        label: formatMessage({
          id: 'workspace.document.skipped',
          defaultMessage: 'Not generated',
        }),
      };
    }
    return null;
  };
  const repeatsEncounterStatus =
    (isGenerating && encounterStatus === 'processing') ||
    (document.status === 'failed' && encounterStatus === 'failed');
  const statusBadge = repeatsEncounterStatus ? null : getStatusBadge();
  const changedSections = sections.flatMap((section) => {
    const code = section.code;
    return code &&
      edits[code] !== undefined &&
      edits[code] !== getSectionText(section)
      ? [{ code, content: edits[code] }]
      : [];
  });
  const canSave =
    canEdit &&
    !!document.id &&
    (document.status === 'completed' || document.status === 'edited');
  const dirty = changedSections.length > 0;
  const blocker = useBlocker(dirty);

  useEffect(() => {
    onDirtyChange(dirty);
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      onDirtyChange(false);
    };
  }, [dirty, onDirtyChange]);

  const cancel = () => {
    setEdits({});
    setFailed(false);
    setEditing(false);
  };

  const regenerate = async () => {
    if (!onRegenerate || regenerating) {
      return;
    }
    setRegenerating(true);
    setRegenerateFailed(false);
    try {
      await onRegenerate();
      setConfirmingRegenerate(false);
    } catch {
      setRegenerateFailed(true);
    } finally {
      setRegenerating(false);
    }
  };

  const cancelRegenerate = () => {
    setConfirmingRegenerate(false);
    setRegenerateFailed(false);
  };

  const save = async () => {
    if (!canSave || !document.id || saving || changedSections.length === 0) {
      return;
    }
    setSaving(true);
    setFailed(false);
    try {
      await onSave(changedSections);
      setEdits({});
      setEditing(false);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-component="workspace-document"
      className={cn(styles.document, className)}
    >
      <DocumentHeader
        title={title}
        tag={
          typeof overallScore === 'number' && (
            <ScoreBadge
              score={overallScore}
              ariaLabel={formatMessage(
                {
                  id: 'workspace.document.overallScore',
                  defaultMessage: 'Overall score: {score, number, ::.#} of 5',
                },
                { score: overallScore },
              )}
            />
          )
        }
        requirement={requirement}
      >
        {statusBadge && (
          <Badge size="small" tone={statusBadge.tone}>
            {statusBadge.label}
          </Badge>
        )}
        {document.approved && (
          <Badge size="small" tone="success">
            <FormattedMessage
              id="workspace.approved"
              defaultMessage="Approved by clinician"
            />
          </Badge>
        )}
        {!editing && actions}
        {onRegenerate && !editing && !isGenerating && (
          <Button
            variant="tertiary"
            size="small"
            disabled={confirmingRegenerate}
            onClick={() => setConfirmingRegenerate(true)}
          >
            <FormattedMessage
              id="global.regenerate"
              defaultMessage="Regenerate"
            />
          </Button>
        )}
        {canSave && !editing && (
          <Button
            variant="secondary"
            size="small"
            disabled={confirmingRegenerate}
            onClick={() => setEditing(true)}
          >
            <FormattedMessage id="global.edit" defaultMessage="Edit" />
          </Button>
        )}
        {editing && (
          <>
            <Button
              variant="tertiary"
              size="small"
              disabled={saving}
              onClick={cancel}
            >
              <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              size="small"
              disabled={!canSave || !dirty || saving}
              loading={saving}
              onClick={save}
            >
              <FormattedMessage id="global.save" defaultMessage="Save" />
            </Button>
          </>
        )}
      </DocumentHeader>
      {confirmingRegenerate && (
        <InlineConfirm
          className={styles.confirm}
          message={
            <FormattedMessage
              id="workspace.document.regenerateConfirm"
              defaultMessage="Regenerate “{name}”? This runs AI processing again for this document only and replaces its text, including clinician edits."
              values={{ name: title }}
            />
          }
          confirmLabel={
            <FormattedMessage
              id="workspace.document.runAi"
              defaultMessage="Run AI processing"
            />
          }
          pending={regenerating}
          onConfirm={regenerate}
          onCancel={cancelRegenerate}
        />
      )}
      {regenerateFailed && (
        <p role="alert" className={cn('p3', styles.alert)}>
          <FormattedMessage
            id="workspace.document.generateFailed"
            defaultMessage="Could not start AI processing. Try again."
          />
        </p>
      )}
      {dirty && (
        <p role="status" className={cn('p3', styles.notice)}>
          <FormattedMessage
            id="workspace.unsaved"
            defaultMessage="Unsaved changes. Save before switching documents or encounters."
          />
        </p>
      )}
      {blocker.state === 'blocked' && (
        <div className={styles.blocker} role="alert">
          <Button
            variant="secondary"
            size="medium"
            onClick={() => blocker.reset()}
          >
            <FormattedMessage
              id="workspace.stay"
              defaultMessage="Keep editing"
            />
          </Button>
          <Button
            variant="secondary"
            size="medium"
            danger
            onClick={() => blocker.proceed()}
          >
            <FormattedMessage
              id="workspace.discardLeave"
              defaultMessage="Discard edits and leave"
            />
          </Button>
        </div>
      )}
      {failed && (
        <p role="alert" className={cn('p3', styles.alert)}>
          <FormattedMessage
            id="workspace.saveFailed"
            defaultMessage="Could not save or refresh the document. Your edits are still here. Try again."
          />
        </p>
      )}
      {document.error && (
        <p role="alert" className={cn('p3', styles.alert)}>
          {document.error}
        </p>
      )}
      <div className={styles.rows}>
        {sections.length === 0 && (
          <p className={cn('p3', styles.empty)}>
            <FormattedMessage
              id="workspace.documentPending"
              defaultMessage="The document will appear after processing."
            />
          </p>
        )}
        {sections.map((section, index) => {
          const code = section.code;
          const text = getSectionText(section);
          const citations = getSectionCitationStarts(section);
          const score = section.score;
          const scoreBadge = typeof score === 'number' && (
            <ScoreBadge
              score={score}
              ariaLabel={formatMessage(
                {
                  id: 'workspace.document.sectionScore',
                  defaultMessage: 'Score: {score, number, ::.#} of 5',
                },
                { score },
              )}
            />
          );
          return editing && code ? (
            <DocumentRow
              key={code}
              as="label"
              label={section.title}
              tag={scoreBadge}
              className={styles.editingRow}
            >
              <Textarea
                value={edits[code] ?? text}
                disabled={saving}
                rows={2}
                isElastic
                textareaClassName={cn('p2', styles.textarea)}
                onChange={(event) =>
                  setEdits((current) => ({
                    ...current,
                    [code]: event.target.value,
                  }))
                }
              />
            </DocumentRow>
          ) : (
            <DocumentRow
              key={code ?? index}
              label={section.title}
              tag={scoreBadge}
            >
              {text ? (
                <span className={cn('p2', styles.text)}>{text}</span>
              ) : (
                <span className={cn('p2', styles.missing)}>
                  <FormattedMessage
                    id="workspace.document.noData"
                    defaultMessage="No content"
                  />
                </span>
              )}
              {(citations.length > 0 || section.user_content) && (
                <span className={styles.chips}>
                  {citations.map((start) => (
                    <span key={start} className={cn('p4', styles.citation)}>
                      <FormattedMessage
                        id="workspace.document.citation"
                        defaultMessage="Recording · {time}"
                        values={{ time: formatTimecode(start) }}
                      />
                    </span>
                  ))}
                  {section.user_content && (
                    <span className={cn('p4', styles.edited)}>
                      <FormattedMessage
                        id="workspace.document.edited"
                        defaultMessage="Edited by clinician"
                      />
                    </span>
                  )}
                </span>
              )}
            </DocumentRow>
          );
        })}
      </div>
      {!!document.references?.length && (
        <ol className={cn('p2', styles.references)}>
          {document.references.map((reference) => (
            <li key={reference.index}>
              {[reference.title, reference.year, reference.journal]
                .filter(Boolean)
                .join(' · ')}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};
